import * as React from 'react';
import { useTranslation } from 'next-i18next';
import styled, { css, keyframes } from 'styled-components';
import { ExclamationCircleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { animated } from '@react-spring/web';

import { Badge, GoogleAutoCompleteInput, Heading } from '@restworld/ui-ds-base';
import { SkeletonTextLoading } from '@restworld/ui-components';
import { addRumTiming, useAnalytics } from '@restworld/utils-common';
import {
  isOrgOnboardingModeAtom,
  orgOnboardingPartnerInfoAtom,
} from '../../organization-onboarding/use-organization-onboarding';
import {
  activeRestaurantOnboardingStateAtom,
  googlePlacesLastStatusAtom,
  googleSearchNoResultSeenAtom,
  isManualEntryAtom,
} from '.';
import { ManualEntryPrompt } from '../../shared/manual-venue-entry';
import { cn } from '@restworld/utils-common';
import { splitGoogleSuggestion } from './manual-entry-prefill';
import { isDeferredBillingCampaign } from '../../organization-onboarding/deferred-billing';
import { tryForFreeAssignmentAtom } from '../../organization-onboarding/try-for-free-variant';

const GOOGLE_PLACES_ERROR_STATUSES = new Set([
  'OVER_QUERY_LIMIT',
  'REQUEST_DENIED',
  'INVALID_REQUEST',
  'UNKNOWN_ERROR',
  'NOT_FOUND',
]);

// How long a no-result query must stay unchanged before we emit the event. Long
// enough to skip mid-typing noise, short enough to still catch quick bouncers.
const NO_RESULT_SETTLE_MS = 2000;

// Passed to GoogleAutoCompleteInput and read by the manual-entry card, so the card follows
// exactly the queries the field actually searches.
const VENUE_MIN_SEARCH_LENGTH = 3;

const defaultInputProps = {
  type: 'text',
  id: '',
};

const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col w-full h-full gap-6 ;', className)} {...props} />
);

const inputAppear = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const inputPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(139, 92, 246, 0), 0 0 6px 0 rgba(139, 92, 246, 0.14);
  }
  50% {
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.14), 0 0 12px 1px rgba(139, 92, 246, 0.26);
  }
`;

const InputPulseWrapper = styled.div<{ $isPulsing: boolean; $skipAppear: boolean }>`
  width: 100%;
  animation: ${inputAppear} 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
  ${({ $skipAppear }) =>
    $skipAppear &&
    css`
      animation: none;
    `}

  ${({ $isPulsing }) =>
    $isPulsing &&
    css`
      & input {
        animation: ${inputPulse} 2s ease-in-out 0.6s infinite;
      }
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    & input {
      animation: none;
      box-shadow: 0 0 6px 0 rgba(139, 92, 246, 0.16);
    }
  }
`;

export const googleAddresInputValueAtom = atom('');
export const activeAddressIdAtom = atom('');
export const activeRestaurantAtom = atom('');
// Name and address of the Google suggestion the employer picked, kept apart (the dropdown joins
// them into `activeRestaurantAtom`). When the venue lookup fails, "Aggiungilo a mano" opens the
// manual form prefilled with them instead of an empty one. Empty when nothing was picked in this
// session (a restored venue, a place id from the URL).
export const activeRestaurantSuggestionAtom = atom<{ name: string; address: string }>({ name: '', address: '' });
// Transient flag: when true, the Google address input mounts with autoFocus.
// Set by the intro CTA handler so the search input is focused immediately on
// transition from the intro screen (mobile keyboard opens).
export const googleAddressInputAutoFocusAtom = atom(false);
// Set by /getting-started when this field takes over from the server-rendered step 1: the
// visitor is already looking at a field there, so fading it in from nothing would make it blink.
export const skipVenueSearchAppearAnimationAtom = atom(false);
// Set by /getting-started when the visitor typed in the server-rendered step 1 before this field
// took over: the value in `googleAddresInputValueAtom` is theirs, so this field searches it on
// mount instead of treating it as a saved value. Consumed once, on mount.
export const venueSearchInitialValueTypedAtom = atom(false);

const GoogleAddressInputResult: React.FunctionComponent = () => {
  const { t } = useTranslation('restaurant-onboarding');
  const [activeItem, setActiveItem] = useAtom(activeAddressIdAtom);
  const [inputValue, setInputValue] = useAtom(googleAddresInputValueAtom);
  const setActiveRestaurantName = useSetAtom(activeRestaurantAtom);
  const setActiveRestaurantSuggestion = useSetAtom(activeRestaurantSuggestionAtom);
  const setShouldAutoFocus = useSetAtom(googleAddressInputAutoFocusAtom);
  const [lastStatus, setLastStatus] = useAtom(googlePlacesLastStatusAtom);
  const skipAppear = useAtomValue(skipVenueSearchAppearAnimationAtom);
  const [initialValueTypedByUser, setInitialValueTypedByUser] = useAtom(venueSearchInitialValueTypedAtom);
  // Read once, for this mount: the flag describes the value this field started from, not the
  // next time a venue search opens (the dashboard's "add venue" modal, a step revisited).
  const initialValueTypedByUserRef = React.useRef(initialValueTypedByUser);

  const inputRef = React.useRef<HTMLDivElement | null>(null);
  const isInputFilled = inputValue.length > 0;

  // Search telemetry for /getting-started only (the same screen also serves the dashboard's
  // "add venue" modal). Two moments, once per mount: the field becomes usable (Google Maps
  // loaded), a RUM timing on the view, and the employer types the first character, a PostHog
  // event. The field is focused on mount, so focus says nothing about intent: the first
  // character does. Together they split "left without typing" from "typed and found nothing",
  // and tell how long the page took to work.
  const isOrgOnboarding = useAtomValue(isOrgOnboardingModeAtom);
  const { trackEvent } = useAnalytics();
  const isFieldReadyRef = React.useRef(false);
  const searchStartedRef = React.useRef(false);

  const handleScriptReady = React.useCallback(() => {
    isFieldReadyRef.current = true;
    if (isOrgOnboarding) addRumTiming('venue_search_ready');
  }, [isOrgOnboarding]);

  // When Google has no usable result the dropdown holds a single "no results"
  // line, so drop its forced min-height — otherwise the 14rem floor leaves a big
  // empty gap and the manual-entry card no longer sits right under the message.
  const hasNoResult = lastStatus === 'ZERO_RESULTS' || GOOGLE_PLACES_ERROR_STATUSES.has(lastStatus);

  // Always focus the search input on mount so the user can start typing
  // immediately. Reset the transient atom flag after consuming it.
  React.useEffect(() => {
    setShouldAutoFocus(false);
    setInitialValueTypedByUser(false);
    const inputEl = inputRef.current?.querySelector<HTMLInputElement>('input');
    inputEl?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToInput = React.useCallback((element: HTMLDivElement) => {
    const rect = element.getBoundingClientRect();
    const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

    if (!isVisible) {
      setTimeout(() => {
        if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          window.scrollTo({ top: rect.top + window.scrollY - 100, behavior: 'smooth' });
        } else {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 750);
    }
  }, []);

  React.useEffect(() => {
    if (isInputFilled && inputRef.current) scrollToInput(inputRef.current);
  }, [isInputFilled, scrollToInput]);

  const handleInputChange = React.useCallback(
    (value: string) => {
      setInputValue(value);
      if (activeItem) setActiveItem(null);
      if (isOrgOnboarding && !searchStartedRef.current && value.length > 0) {
        searchStartedRef.current = true;
        trackEvent('employer_onboarding_venue_search_started', {
          ms_since_page_load: Math.round(performance.now()),
          field_ready: isFieldReadyRef.current,
        });
      }
    },
    [activeItem, setActiveItem, setInputValue, isOrgOnboarding, trackEvent]
  );

  const handleDropdownItemClick = React.useCallback(
    (selectedItem: string, restaurantName?: string, address?: string) => {
      setActiveItem(selectedItem);
      setActiveRestaurantName(restaurantName);
      setActiveRestaurantSuggestion(splitGoogleSuggestion(restaurantName, address));
    },
    [setActiveItem, setActiveRestaurantName, setActiveRestaurantSuggestion]
  );

  const inputProps = {
    ...defaultInputProps,
    icon: MagnifyingGlassIcon,
    bordercolor: 'primary' as const,
    hasLeftPadding: true,
    autoFocus: false,
    wrapperStyle: 'w-full',
    onChange: handleInputChange,
    value: inputValue,
    isFullwidth: true,
  };

  const dropdownItemProps = {
    onClick: handleDropdownItemClick,
    activeItem,
    itemRenderVariant: 'without_break_line' as const,
    loadingElement: (
      <li className="flex flex-col gap-4">
        <SkeletonTextLoading className="!w-full !h-[2.5rem]" width="rect-box" />
        <SkeletonTextLoading className="!w-full !h-[2.5rem]" width="rect-box" />
        <SkeletonTextLoading className="!w-full !h-[2.5rem]" width="rect-box" />
      </li>
    ),
    // The search only finds businesses, so a street address or a job title finds nothing
    // (most no-result queries, 22-27 Oct 2026): say what to type instead (RW-410).
    noResultElement: (
      <li>
        <span className="flex items-center gap-1">
          <ExclamationCircleIcon className="h-5 w-5 text-neutral-700" />
          <Heading as="span" size={6} weight="normal" color="neutral" className="leading-5.25">
            {t('venue_search_no_result')}
          </Heading>
        </span>
      </li>
    ),
  };

  return (
    <animated.div
      style={{
        transform: !isInputFilled ? 'translateY(4%)' : 'translateY(0%)',
        transition: 'transform 0.3s ease-in-out',
      }}
      className="w-full"
    >
      <Wrapper>
        {!isInputFilled && <OnboardingTitle />}
        <div className="flex flex-col gap-2 w-full">
          <InputPulseWrapper ref={inputRef} $isPulsing={!isInputFilled} $skipAppear={skipAppear}>
            <GoogleAutoCompleteInput
              placesFilterTypes={['establishment']}
              formId="search-address-input"
              key="search-address-input"
              trackingSurface="employer_restaurant_onboarding_venue"
              placeholder={t('venue_search_placeholder')}
              minSearchLength={VENUE_MIN_SEARCH_LENGTH}
              inputProps={inputProps}
              dropdownItemProps={dropdownItemProps}
              mainWrapperStyle={hasNoResult ? 'w-full flex flex-col gap-2' : 'w-full flex flex-col gap-8'}
              dropdownMainWrapperStyle={'!relative border-none shadow-none lg:max-w-full lg:mt-0 !bg-transparent'}
              dropdownSubWrapperStyle={
                hasNoResult
                  ? 'max-h-full overflow-x-hidden px-0 py-0 min-h-0'
                  : 'max-h-full overflow-x-hidden px-0 py-0'
              }
              withFloatingUI={false}
              placesLocationBias={{
                south: 35.49,
                west: 6.63,
                north: 47.09,
                east: 18.52,
              }}
              onStatusChange={setLastStatus}
              onScriptReady={handleScriptReady}
              initialValueTypedByUser={initialValueTypedByUserRef.current}
            />
          </InputPulseWrapper>
        </div>
        <ManualEntryLink />
      </Wrapper>
    </animated.div>
  );
};

const OnboardingTitle = () => {
  const { t } = useTranslation('restaurant-onboarding');
  const isOrgOnboarding = useAtomValue(isOrgOnboardingModeAtom);
  const partnerInfo = useAtomValue(orgOnboardingPartnerInfoAtom);
  // Deferred billing campaigns (Try for Free): the reminder replaces the campaign intro
  // screen. The number of days comes from the campaign; one key, so the copy can follow
  // the ads without touching code.
  const showFreeDaysReminder = isOrgOnboarding && isDeferredBillingCampaign(partnerInfo);
  // Try for Free A/B test (RW-411), option 0: the `simplified` variant says why we ask for the
  // venue, in place of the generic subtitle.
  const tryForFreeAssignment = useAtomValue(tryForFreeAssignmentAtom);
  const subtitleKey =
    showFreeDaysReminder && tryForFreeAssignment?.variant === 'simplified'
      ? 'try_for_free_venue_subtitle'
      : 'let_s_get_started_subtitle';

  return (
    <div className="self-start flex flex-col justify-start gap-1">
      {showFreeDaysReminder && partnerInfo && (
        <Badge
          variant="restworld"
          data-testid="deferred-billing-reminder"
          className="self-start mb-3 h-auto rounded-full border border-purple-700 px-3 py-1 font-semibold"
        >
          {t('deferred_billing_reminder', { count: partnerInfo.free_days })}
        </Badge>
      )}
      <Heading size={4} color="neutral">
        {t('let_s_get_started')}
      </Heading>
      <Heading size={4} color="neutral">
        {t(isOrgOnboarding ? 'which_venue_are_you_looking_to_hire_staff_for' : 'search_your_restaurant_name')}
      </Heading>
      <p className="text-base font-normal text-neutral-500 m-0 mt-2">{t(subtitleKey)}</p>
    </div>
  );
};

const ManualEntryLink = () => {
  const setActiveUI = useSetAtom(activeRestaurantOnboardingStateAtom);
  const setIsManualEntry = useSetAtom(isManualEntryAtom);
  const lastStatus = useAtomValue(googlePlacesLastStatusAtom);
  const lastQuery = useAtomValue(googleAddresInputValueAtom);
  const setNoResultSeen = useSetAtom(googleSearchNoResultSeenAtom);
  const { trackEvent } = useAnalytics();

  // A "no usable result" response from the Google Places API: either an explicit
  // ZERO_RESULTS, or an API-level error (quota exceeded, request denied, ...).
  // GoogleAutoCompleteInput searches only from VENUE_MIN_SEARCH_LENGTH characters and leaves
  // the last status in place below that, so a field cleared after a failed search would keep
  // the card: the status only counts while the query is long enough to have produced it.
  const isZeroResults = lastStatus === 'ZERO_RESULTS';
  const isApiError = !!lastStatus && GOOGLE_PLACES_ERROR_STATUSES.has(lastStatus);
  const hasNoResult = (isZeroResults || isApiError) && (lastQuery || '').trim().length >= VENUE_MIN_SEARCH_LENGTH;

  // De-dupe per *distinct* query string (not once-per-session): the same failed
  // search is never reported twice, but every different venue name that returns
  // nothing is captured — a user who fails on 3 names is a stronger signal than one.
  const reportedNoResultQueriesRef = React.useRef<Set<string>>(new Set());

  // Fire when the Google Places API reports no results, independent of whether the
  // user goes on to click "enter manually". This measures how often a venue genuinely
  // can't be found via Google. We wait for the query to *settle* (NO_RESULT_SETTLE_MS
  // of no further changes) so mid-typing noise and partial gibberish don't fire:
  // every keystroke/status change re-runs this effect and clears the pending timer,
  // so we only emit once the user has paused on a query Google found nothing for.
  React.useEffect(() => {
    const query = (lastQuery || '').trim();
    if (!hasNoResult || query.length < 3) return;
    if (reportedNoResultQueriesRef.current.has(query)) return;

    const timer = setTimeout(() => {
      reportedNoResultQueriesRef.current.add(query);
      const reason = isZeroResults ? 'zero_results' : 'api_error';
      trackEvent('employer_onboarding_google_search_no_result', {
        trigger: 'gmaps_api',
        reason,
        ...(reason === 'api_error' ? { error_code: lastStatus } : {}),
        last_query: query,
      });
      setNoResultSeen(true);
    }, NO_RESULT_SETTLE_MS);

    return () => clearTimeout(timer);
  }, [hasNoResult, isZeroResults, lastStatus, lastQuery, trackEvent, setNoResultSeen]);

  const handleClick = React.useCallback(() => {
    if (hasNoResult) {
      const reason = isZeroResults ? 'zero_results' : 'api_error';
      trackEvent('employer_onboarding_google_search_no_result', {
        trigger: 'manual_entry_click',
        reason,
        ...(reason === 'api_error' ? { error_code: lastStatus } : {}),
        last_query: lastQuery,
      });
      setNoResultSeen(true);
    }
    setIsManualEntry(true);
    setActiveUI('restaurant_onboarding_form');
  }, [setActiveUI, setIsManualEntry, hasNoResult, isZeroResults, lastStatus, lastQuery, setNoResultSeen, trackEvent]);

  // Promote the manual-entry prompt to a high-affordance card when Google has no match.
  return <ManualEntryPrompt visible emphasized={hasNoResult} onClick={handleClick} />;
};

export default GoogleAddressInputResult;
